import React, { useState } from 'react';
import { BULK_VOUCHERS_PERCENTAGES, BULK_VOUCHERS_STATUS } from 'constants/vouchers';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';

import { CREATE_BULK_VOUCHERS, DELETE_BULK_VOUCHERS } from 'store/mutations';
import { GET_BULK_VOUCHERS_METADATA } from 'store/queries';
import AgentPortalError from 'errors/AgentPortalError';
import { FormattedMessage } from 'react-intl';
import { ActionButton } from 'components/ui/Button';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import StyledCalendar from 'components/ui/StyledCalendar';
import { downloadBulkVouchers, uploadBulkVouchers } from 'utils/bulkVouchers';

import {
  ErrorField,
  Field,
  MandatoryFieldLabel,
  StyledActionButtonContainers,
  StyledContainer,
  StyledForm,
  StyledInput,
  StyledMetadataContainer,
  StyledMetadataLabel,
  StyledMetadataRow,
  StyledMetadataValue,
  StyledRow,
  StyledSelect,
  StyledTitle,
  StyledTravelDate,
  StyledLabel,
  StyledLink
} from './GenerateBulkVouchers.styles';

const GenerateBulkVouchers = ({ user }) => {
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [uploadBulkVouchersFileError, setUploadBulkVouchersFileError] = useState(null);
  const [notFoundCreationError, setNotFoundCreationError] = useState(false);
  const [vouchersMetadata, setVouchersMetadata] = useState(null);

  const history = useHistory();
  const { register, handleSubmit, formState, setValue, watch, getValues, reset } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      serviceNumber: '',
      travelDate: moment().subtract(1, 'day').toDate(),
      compensationPercentage: String(BULK_VOUCHERS_PERCENTAGES[0])
    }
  });

  const [getBulkVouchersMetadata, { loading: loadingGetBulkVouchersMetadata, error: errorGetBulkVouchersMetadata }] = useLazyQuery(
    GET_BULK_VOUCHERS_METADATA,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setVouchersMetadata(data.getBulkVouchersMetadata);
      }
    }
  );

  const [deleteBulkVouchers, { loading: loadingDeleteBulkVouchers, error: errorDeleteBulkVouchers }] = useMutation(DELETE_BULK_VOUCHERS, {
    onCompleted: () => {
      setVouchersMetadata(null);
    }
  });

  const [createBulkVouchers, { loading: loadingCreateBulkVouchers, error: errorCreateBulkVouchers }] = useMutation(CREATE_BULK_VOUCHERS, {
    onCompleted: (data) => {
      if (!data?.createBulkVouchers) {
        setNotFoundCreationError(true);
        return;
      }

      const { serviceNumber, travelDate } = getValues();

      getBulkVouchersMetadata({
        variables: {
          serviceNumber,
          travelDate: moment(travelDate).format('YYYY-MM-DD')
        }
      });
    }
  });

  const maxTraveDate = moment().subtract(1, 'day').toDate();
  const travelDate = watch('travelDate');
  const { errors } = formState;

  const error = errorGetBulkVouchersMetadata || errorCreateBulkVouchers || errorDeleteBulkVouchers;
  const loading = loadingGetBulkVouchersMetadata || loadingCreateBulkVouchers || loadingDeleteBulkVouchers || isFileUploading;

  useEffect(() => {
    if (!user.canManageGenerateBulkVouchers) {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCreateBulkVouchers = ({ serviceNumber, travelDate, compensationPercentage }) => {
    setNotFoundCreationError(false);
    setUploadBulkVouchersFileError(null);
    createBulkVouchers({
      variables: {
        serviceNumber,
        travelDate: moment(travelDate).format('YYYY-MM-DD'),
        compensationPercentage: Number(compensationPercentage)
      }
    });
  };

  const handleDeleteBulkVouchers = () => {
    reset();
    setUploadBulkVouchersFileError(null);
    deleteBulkVouchers({
      variables: {
        serviceNumber: vouchersMetadata.serviceNumber,
        travelDate: vouchersMetadata.travelDate
      }
    });
  };

  const handleUploadFile = async (event) => {
    setIsFileUploading(true);
    setUploadBulkVouchersFileError(null);

    const file = event.target.files[0];

    const formData = new FormData();
    formData.append('file', file);

    if (file) {
      event.target.value = '';
    }

    try {
      await uploadBulkVouchers(formData);

      const { serviceNumber, travelDate } = getValues();

      await getBulkVouchersMetadata({
        variables: {
          serviceNumber,
          travelDate: moment(travelDate).format('YYYY-MM-DD')
        }
      });
    } catch (error) {
      setUploadBulkVouchersFileError(error?.response?.data || error?.message);
    } finally {
      setIsFileUploading(false);
    }
  };

  const handleSetTravelDate = (value) => {
    setValue('travelDate', value);
  };

  const downloadFile = (filename) => (event) => {
    event.preventDefault();
    downloadBulkVouchers(filename);
  };

  if (error) {
    throw new AgentPortalError(error);
  }

  return (
    <StyledContainer>
      <StyledTitle>
        <FormattedMessage id="bulkVouchers.generateTitle" />
      </StyledTitle>
      <StyledForm data-testid="generate-bulk-vouchers-form" onSubmit={handleSubmit(handleCreateBulkVouchers)}>
        <Field>
          <MandatoryFieldLabel htmlFor="travelDate">
            <FormattedMessage id="bulkVouchers.travelDate" />
          </MandatoryFieldLabel>
          <StyledTravelDate>{moment(travelDate).format('dddd DD MMMM YYYY')}</StyledTravelDate>
          <StyledCalendar
            {...register('travelDate', { required: 'dateError' })}
            name="travelDate"
            showNeighboringMonth={false}
            tabIndex="-1"
            maxDate={maxTraveDate}
            value={travelDate}
            onChange={handleSetTravelDate}
          />
          {errors.travelDate && (
            <ErrorField id="error-travelDate" data-testid="error-travelDate">
              <FormattedMessage id="bulkVouchers.serviceNumberValidationMessage" />
            </ErrorField>
          )}
        </Field>
        <StyledRow>
          <Field>
            <MandatoryFieldLabel htmlFor="serviceNumber">
              <FormattedMessage id="bulkVouchers.serviceNumber" />
            </MandatoryFieldLabel>
            <StyledInput
              {...register('serviceNumber', { required: true, pattern: /^[0-9]{4}$/ })}
              id="serviceNumber"
              type="number"
              data-cy="serviceNumber-input"
              data-testid="serviceNumber-input"
            />
            {errors.serviceNumber && (
              <ErrorField id="error-serviceNumber" data-testid="error-serviceNumber">
                <FormattedMessage id="bulkVouchers.serviceNumberValidationMessage" />
              </ErrorField>
            )}
          </Field>
          <Field>
            <MandatoryFieldLabel htmlFor="compensationPercentage">
              <FormattedMessage id="bulkVouchers.compensationPercentage" />
            </MandatoryFieldLabel>
            <StyledSelect
              {...register('compensationPercentage', { required: true })}
              id="compensationPercentage"
              data-cy="compensation-percentage-dropdown"
              data-testid="compensation-percentage-dropdown"
            >
              {BULK_VOUCHERS_PERCENTAGES.map((value) => (
                <option key={value} value={String(value)} data-testid={`compensation-percentage-${value}`}>
                  {`${value}%`}
                </option>
              ))}
            </StyledSelect>
          </Field>
        </StyledRow>
        {notFoundCreationError && (
          <ErrorField id="error-notFoundCreation" data-testid="error-notFoundCreation">
            <FormattedMessage id="bulkVouchers.notFoundDataForCreationMessage" />
          </ErrorField>
        )}
        <StyledActionButtonContainers>
          <ActionButton data-cy="create-by-details" data-testid="create-by-details" type="submit" disabled={loading} loading={loading}>
            <FormattedMessage id="bulkVouchers.generate" />
          </ActionButton>
        </StyledActionButtonContainers>
        {vouchersMetadata && vouchersMetadata?.status && (
          <StyledMetadataContainer data-testid="bulk-vouchers-metadata-container" data-cy="bulk-vouchers-metadata-container">
            <StyledMetadataRow>
              <StyledMetadataLabel>
                <FormattedMessage id="bulkVouchers.status" />:
              </StyledMetadataLabel>
              <StyledMetadataValue>
                <FormattedMessage id={`bulkVouchers.status.${vouchersMetadata.status}`} />
              </StyledMetadataValue>
            </StyledMetadataRow>
            <StyledMetadataRow>
              <StyledMetadataLabel>
                <FormattedMessage id="bulkVouchers.selectedTickets" />:
              </StyledMetadataLabel>
              <StyledMetadataValue>
                {vouchersMetadata.selectedTotalCount}
                {vouchersMetadata.selectedTotalCount > 0 ? (
                  <>
                    {' '}
                    -{' '}
                    <StyledLink type="button" onClick={downloadFile(vouchersMetadata.selectedFile)}>
                      {vouchersMetadata.selectedFile}
                    </StyledLink>
                  </>
                ) : (
                  ''
                )}
              </StyledMetadataValue>
            </StyledMetadataRow>
            <StyledMetadataRow>
              <StyledMetadataLabel>
                <FormattedMessage id="bulkVouchers.ignoredTickets" />:
              </StyledMetadataLabel>
              <StyledMetadataValue>
                {vouchersMetadata.ignoredTotalCount}
                {vouchersMetadata.ignoredTotalCount > 0 ? (
                  <>
                    {' '}
                    -{' '}
                    <StyledLink type="button" onClick={downloadFile(vouchersMetadata.ignoredFile)}>
                      {vouchersMetadata.ignoredFile}
                    </StyledLink>
                  </>
                ) : (
                  ''
                )}
              </StyledMetadataValue>
            </StyledMetadataRow>
            {vouchersMetadata.voucherType && (
              <StyledMetadataRow>
                <StyledMetadataLabel>
                  <FormattedMessage id="bulkVouchers.vouchersType" />:
                </StyledMetadataLabel>
                <StyledMetadataValue>
                  <FormattedMessage
                    id="bulkVouchers.vouchersTypePercentage"
                    values={{ voucherType: vouchersMetadata.voucherType, percentage: vouchersMetadata.compensationPercentage }}
                  />
                </StyledMetadataValue>
              </StyledMetadataRow>
            )}
            {vouchersMetadata.selectedTotalPriceAmount.length > 0 && (
              <StyledMetadataRow>
                <StyledMetadataLabel>
                  <FormattedMessage id="bulkVouchers.selectedCompensationAmount" />:
                </StyledMetadataLabel>
                <StyledMetadataValue>
                  {vouchersMetadata.selectedTotalPriceAmount.reduce((acc, { amount, currency }, index, array) => {
                    acc.push(<FormattedCurrency key={currency + amount} amount={amount} currency={currency} />);
                    if (index < array.length - 1) {
                      acc.push(', ');
                    }
                    return acc;
                  }, [])}
                </StyledMetadataValue>
              </StyledMetadataRow>
            )}
          </StyledMetadataContainer>
        )}
        <StyledActionButtonContainers>
          {vouchersMetadata?.status && vouchersMetadata.status === BULK_VOUCHERS_STATUS.READY && (
            <>
              <ActionButton
                type="button"
                data-cy="delete-bulk-vouchers"
                data-testid="delete-bulk-vouchers"
                onClick={handleDeleteBulkVouchers}
                disabled={loading}
                loading={loading}
              >
                <FormattedMessage id="bulkVouchers.delete" />
              </ActionButton>
              <ActionButton
                type="button"
                data-cy="upload-bulk-vouchers-file"
                data-testid="upload-bulk-vouchers-file"
                disabled={loading}
                loading={loading}
              >
                <StyledLabel htmlFor="upload-bulk-vouchers-file-input">
                  <FormattedMessage id="bulkVouchers.upload" />
                  <input
                    id="upload-bulk-vouchers-file-input"
                    data-testid="upload-bulk-vouchers-file-input"
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={handleUploadFile}
                  />
                </StyledLabel>
              </ActionButton>
            </>
          )}
        </StyledActionButtonContainers>
        {uploadBulkVouchersFileError && (
          <ErrorField id="error-uploadBulkVouchersFileError" data-testid="error-uploadBulkVouchersFileError">
            <FormattedMessage id="bulkVouchers.uploadBulkVouchersFileError" />: {uploadBulkVouchersFileError}
          </ErrorField>
        )}
      </StyledForm>
    </StyledContainer>
  );
};

GenerateBulkVouchers.propTypes = {
  user: PropTypes.shape({
    canManageGenerateBulkVouchers: PropTypes.bool
  }),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default GenerateBulkVouchers;
